import { Sex } from './lib/__generated/patient-api-sdk';
import { RelationToSubscriber } from './lib/__generated/patient-sdk';
import constantStrings from './utils/constant-strings.en';

import config from '@src/configs/aspendental';

export const INTERVAL_LENGTH = 300;
export const TIMEOUT_WARNING_LENGTH = 30;
export const TIMEOUT_WARNING_BUFFER = 10;

export const ASPEN_DENTAL_LOGO_LIGHT =
	'https://images.ctfassets.net/m8zwsu9tyucg/1OLI6iOYAFp0Mvqm46Yyor/8908782b135cb2d577fe727a536434b1/logo-aspendental-white.svg';

export const EXPERIAN_SEARCH_API = 'https://api.experianaperture.io/address/search/v1';
export const EXPERIAN_VALIDATE_API = 'https://api.experianaperture.io/address/validate/v1';

export const GOOGLE_RESERVE_PARTNER_ID = 20002677;
export const APP_PROD_BASE_URL = 'https://www.aspendental.com';
export const APP_STAGE_BASE_URL = 'https://wwwstg.aspendental.com';
export const FALLBACK_FACILITY_PHONE_NUMBER = '8002773633';
export const PATIENT_SERVICES_BASE_URL = 'patient-services';
export const SCHEDULER_BASE_URL = 'schedule-an-appointment';
export const FORMS_BASE_URL = 'patient-services/forms';
export const ACCOUNT_INFORMATION_BASE_URL = 'patient-services/account-information';
export const WALKOUT_RECEIPT_BASE_URL = 'patient-services/walkout-receipt';
export const SESSION_KEY = 'aspendental_session_active';

export const DOB_LENGTH = 10;
export const DOB_LIMIT = 1900;
export const ADD_INSURANCE_LIMIT = 4;
export const PHONE_NUMBER_LENGTH = 10;
export const US_POSTAL_CODE_LENGTH = 5;
export const CA_POSTAL_CODE_LENGTH = 7;
export const CARD_AUTHORIZATION_AGE = 18;
export const FORMATTED_PHONE_NUMBER_LENGTH = 14;
export const WEIGHT_LIMIT = {
	MIN: 10,
	MAX: 990,
};

export enum Locale {
	English = 'en-US',
}

export enum TagTheme {
	AspenDental = 'aspendental',
	MottoAligners = 'motto',
	AzPetVet = 'azPetVet',
	TeamTag = 'teamTag',
	WellNow = 'wellnow',
	Chapter = 'chapter',
	ClearChoice = 'clearchoice',
}
const { formNodeStrings, formNodeStringsES } = constantStrings;
/**
 * @collection ORDERED_FORM_NODES
 *
 * This structure contains global data related to the specific form
 * sections available to a user.
 */
export const ORDERED_FORM_NODES = Object.freeze([
	{ sectionName: formNodeStrings.patientInfo, sectionNameES: formNodeStringsES.patientInfo, enabledByDefault: true },
	{ sectionName: formNodeStrings.dateTime, sectionNameES: formNodeStringsES.dateTime, enabledByDefault: true },
	{
		sectionName: formNodeStrings.additionalInfo,
		sectionNameES: formNodeStringsES.additionalInfo,
		enabledByDefault: true,
	},
	{
		sectionName: formNodeStrings.guarantorInfo,
		sectionNameES: formNodeStringsES.guarantorInfo,
		enabledByDefault: false,
	},
	{
		sectionName: formNodeStrings.apptReview,
		sectionNameES: formNodeStringsES.apptReview,
		enabledByDefault: true,
		headerTitle: 'Review & Confirm Appointment',
	},
	{
		sectionName: formNodeStrings.confirmation,
		sectionNameES: formNodeStringsES.confirmation,
		enabledByDefault: true,
	},
]);

export const MOTTO_FORM_NODES = Object.freeze([
	{ sectionName: formNodeStrings.dateTime, sectionNameES: formNodeStringsES.dateTime, enabledByDefault: true },
	{ sectionName: formNodeStrings.patientInfo, sectionNameES: formNodeStringsES.patientInfo, enabledByDefault: true },
	{
		sectionName: formNodeStrings.guarantorInfo,
		sectionNameES: formNodeStringsES.guarantorInfo,
		enabledByDefault: false,
	},
	{
		sectionName: formNodeStrings.apptReview,
		sectionNameES: formNodeStringsES.apptReview,
		enabledByDefault: true,
		headerTitle: 'Review & Confirm Appointment',
	},
	{
		sectionName: formNodeStrings.confirmation,
		sectionNameES: formNodeStringsES.confirmation,
		enabledByDefault: true,
	},
]);

export const V2_FORM_NODES = Object.freeze([
	{ sectionName: formNodeStrings.patientInfo, sectionNameES: formNodeStringsES.patientInfo, enabledByDefault: true },
	{ sectionName: formNodeStrings.dateTime, sectionNameES: formNodeStringsES.dateTime, enabledByDefault: true },
	{
		sectionName: formNodeStrings.guarantorInfo,
		sectionNameES: formNodeStringsES.guarantorInfo,
		enabledByDefault: false,
	},
	{
		sectionName: formNodeStrings.apptReview,
		sectionNameES: formNodeStringsES.apptReview,
		enabledByDefault: true,
		headerTitle: 'Review & Confirm Appointment',
	},
	{
		sectionName: formNodeStrings.confirmation,
		sectionNameES: formNodeStringsES.confirmation,
		enabledByDefault: true,
	},
]);

export const PREFIX = '/api/v1/schedule-an-appointment';

export const APP_CONFIGURATIONS = Object.freeze({
	services: {
		prod: {
			EAPI_REST_URL: 'https://api.aspendental.com/api/web/',
			EAPI_GRAPHQL_URL: 'https://api.aspendental.com/websitegraphql/graphql/',
			GOOGLE_RESERVE_API: 'https://www.google.com/maps/conversion/collect',
			USER_EVENTS_URL: 'https://api.teamtag.com/v1/aspendental/dataplatform/userevents/',
			FORM_EVENTS_URL: 'https://api.teamtag.com/form/events',
			GOOGLE_MAP_API_KEY: config.services.prod.GOOGLE_MAP_API_KEY,
			EAPI_BASE_URL: 'https://api.aspendental.com/',
			PREVISIT_PAPERWORK_URL: 'https://visitprep.aspendental.com/login?token=',
			CONFIG_URL: 'https://www.aspendental.com/js/config.js',
			LOADER_URL: 'https://www.aspendental.com/js/loader_5.8.1.js',
		},
		nonprod: {
			EAPI_REST_URL: 'https://apistg.aspendental.com/api/web/',
			EAPI_GRAPHQL_URL: 'https://apistg.aspendental.com/websitegraphql/graphql/',
			GOOGLE_RESERVE_API: 'https://www.google.com/maps/conversion/debug/collect',
			USER_EVENTS_URL: 'https://aspen-userevents.gcp.stage.aspendental.com/',
			FORM_EVENTS_URL: 'https://aspen-userevents.gcp.stage.aspendental.com/form/events',
			GOOGLE_MAP_API_KEY: config.services.nonprod.GOOGLE_MAP_API_KEY,
			EAPI_BASE_URL: 'https://apistg.aspendental.com/',
			PREVISIT_PAPERWORK_URL: 'https://visitprepstg.aspendental.com/login?token=',
			CONFIG_URL: 'https://wwwstg.aspendental.com/js/config.js',
			LOADER_URL: 'https://wwwstg.aspendental.com/js/loader_5.8.1.js',
		},
	},
});

export const GUARANTOR_RELATIONSHIPS = ['Parent/Guardian', 'Grandmother', 'Grandfather', 'Aunt', 'Uncle', 'Other'];

export const SITE_META_TITLE = {
	schedule: 'Schedule an Appointment | Aspen Dental',
	portal: 'Patient Service Portal | Aspen Dental',
};

export const SITE_META_DESCRIPTIONS = {
	standard:
		"It's easy to schedule an appointment with Aspen Dental. In a few steps, you will be able to book an appointment for you or your loved ones.",
	motto: 'Aspen Dental dentists are committed to your short and long-term oral health needs. Schedule an appointment with a dentist near you.',
	portal: 'Manage your appointment, complete patient forms, and pay your bill all in one place.',
};

export enum DataLayerStepNames {
	timeSelection = 'date_time_selection',
	findLocation = 'find_a_location',
	additionalPatientInfo = 'additional_patient_information',
	patientInformation = 'patient_information',
	reasonForVisit = 'reason_for_visit',
	reasonSelect = 'reason_selected',
	review = 'review',
	confirmation = 'confirmation',
}

export enum AppointmentErrorMessages {
	onlineUnavailable = "Unfortunately, you can't currently schedule this kind of appointment online.",
	statusCode503 = 'Our online scheduling experience is temporarily down for maintenance.',
}

export const MOBILE_STEPPER_ACTIVE = true;

export const CACHE_CONTROL_NO_CACHE = 'no-cache, no-store, must-revalidate';
export const SENTRY_DSN =
	'https://7dc7e942bc8328c0551b695c0e3ea48a@o4508615315554304.ingest.us.sentry.io/4509005914046464';

export const SEX_OPTIONS = (t: (key: string) => string) => [
	{ value: Sex.Male, label: t('personal.sexOptions.male') },
	{ value: Sex.Female, label: t('personal.sexOptions.female') },
	{ value: Sex.Other, label: t('personal.sexOptions.other') },
	{ value: Sex.Unspecified, label: t('personal.sexOptions.unspecified') },
];

type Translator = (key: string) => string;

export const RELATIONSHIP_OPTIONS = (t: Translator) => [
	{ value: RelationToSubscriber.Wife, label: t('insuranceModal.relationshipOptions.wife') },
	{ value: RelationToSubscriber.Child, label: t('insuranceModal.relationshipOptions.child') },
	{ value: RelationToSubscriber.Husband, label: t('insuranceModal.relationshipOptions.husband') },
	{ value: RelationToSubscriber.OtherDependant, label: t('insuranceModal.relationshipOptions.otherDependent') },
	{
		value: RelationToSubscriber.DisabledDependant,
		label: t('insuranceModal.relationshipOptions.disabledDependent'),
	},
];

export const STATE_OPTIONS = (t: (key: string) => string) => [
	{ label: t('locationOptions.state.alabama'), value: 'US-AL' },
	{ label: t('locationOptions.state.alaska'), value: 'US-AK' },
	{ label: t('locationOptions.state.americanSamoa'), value: 'US-AS' },
	{ label: t('locationOptions.state.arizona'), value: 'US-AZ' },
	{ label: t('locationOptions.state.arkansas'), value: 'US-AR' },
	{ label: t('locationOptions.state.california'), value: 'US-CA' },
	{ label: t('locationOptions.state.colorado'), value: 'US-CO' },
	{ label: t('locationOptions.state.connecticut'), value: 'US-CT' },
	{ label: t('locationOptions.state.delaware'), value: 'US-DE' },
	{ label: t('locationOptions.state.districtOfColumbia'), value: 'US-DC' },
	{ label: t('locationOptions.state.florida'), value: 'US-FL' },
	{ label: t('locationOptions.state.georgia'), value: 'US-GA' },
	{ label: t('locationOptions.state.guam'), value: 'US-GU' },
	{ label: t('locationOptions.state.hawaii'), value: 'US-HI' },
	{ label: t('locationOptions.state.idaho'), value: 'US-ID' },
	{ label: t('locationOptions.state.illinois'), value: 'US-IL' },
	{ label: t('locationOptions.state.indiana'), value: 'US-IN' },
	{ label: t('locationOptions.state.iowa'), value: 'US-IA' },
	{ label: t('locationOptions.state.kansas'), value: 'US-KS' },
	{ label: t('locationOptions.state.kentucky'), value: 'US-KY' },
	{ label: t('locationOptions.state.louisiana'), value: 'US-LA' },
	{ label: t('locationOptions.state.maine'), value: 'US-ME' },
	{ label: t('locationOptions.state.maryland'), value: 'US-MD' },
	{ label: t('locationOptions.state.massachusetts'), value: 'US-MA' },
	{ label: t('locationOptions.state.michigan'), value: 'US-MI' },
	{ label: t('locationOptions.state.minnesota'), value: 'US-MN' },
	{ label: t('locationOptions.state.mississippi'), value: 'US-MS' },
	{ label: t('locationOptions.state.missouri'), value: 'US-MO' },
	{ label: t('locationOptions.state.montana'), value: 'US-MT' },
	{ label: t('locationOptions.state.nebraska'), value: 'US-NE' },
	{ label: t('locationOptions.state.nevada'), value: 'US-NV' },
	{ label: t('locationOptions.state.newHampshire'), value: 'US-NH' },
	{ label: t('locationOptions.state.newJersey'), value: 'US-NJ' },
	{ label: t('locationOptions.state.newMexico'), value: 'US-NM' },
	{ label: t('locationOptions.state.newYork'), value: 'US-NY' },
	{ label: t('locationOptions.state.northCarolina'), value: 'US-NC' },
	{ label: t('locationOptions.state.northDakota'), value: 'US-ND' },
	{
		label: t('locationOptions.state.northernMarianaIslands'),
		value: 'US-MP',
	},
	{ label: t('locationOptions.state.ohio'), value: 'US-OH' },
	{ label: t('locationOptions.state.oklahoma'), value: 'US-OK' },
	{ label: t('locationOptions.state.oregon'), value: 'US-OR' },
	{ label: t('locationOptions.state.pennsylvania'), value: 'US-PA' },
	{ label: t('locationOptions.state.puertoRico'), value: 'US-PR' },
	{ label: t('locationOptions.state.rhodeIsland'), value: 'US-RI' },
	{ label: t('locationOptions.state.southCarolina'), value: 'US-SC' },
	{ label: t('locationOptions.state.southDakota'), value: 'US-SD' },
	{ label: t('locationOptions.state.tennessee'), value: 'US-TN' },
	{ label: t('locationOptions.state.texas'), value: 'US-TX' },
	{ label: t('locationOptions.state.utah'), value: 'US-UT' },
	{ label: t('locationOptions.state.vermont'), value: 'US-VT' },
	{ label: t('locationOptions.state.virginIslands'), value: 'US-VI' },
	{ label: t('locationOptions.state.virginia'), value: 'US-VA' },
	{ label: t('locationOptions.state.washington'), value: 'US-WA' },
	{ label: t('locationOptions.state.westVirginia'), value: 'US-WV' },
	{ label: t('locationOptions.state.wisconsin'), value: 'US-WI' },
	{ label: t('locationOptions.state.wyoming'), value: 'US-WY' },
];

export const PROVINCE_OPTIONS = (t: (key: string) => string) => [
	{ label: t('locationOptions.province.alberta'), value: 'CA-AB' },
	{ label: t('locationOptions.province.britishColumbia'), value: 'CA-BC' },
	{ label: t('locationOptions.province.manitoba'), value: 'CA-MB' },
	{ label: t('locationOptions.province.newBrunswick'), value: 'CA-NB' },
	{
		label: t('locationOptions.province.newfoundlandAndLabrador'),
		value: 'CA-NL',
	},
	{ label: t('locationOptions.province.novaScotia'), value: 'CA-NS' },
	{ label: t('locationOptions.province.ontario'), value: 'CA-ON' },
	{
		label: t('locationOptions.province.princeEdwardIsland'),
		value: 'CA-PE',
	},
	{ label: t('locationOptions.province.quebec'), value: 'CA-QC' },
	{ label: t('locationOptions.province.saskatchewan'), value: 'CA-SK' },
	{
		label: t('locationOptions.province.northwestTerritories'),
		value: 'CA-NT',
	},
	{ label: t('locationOptions.province.nunavut'), value: 'CA-NU' },
	{ label: t('locationOptions.province.yukon'), value: 'CA-YT' },
];

export const COUNTRY_OPTIONS = (t: (key: string) => string) => [
	{ label: t('locationOptions.country.unitedStates'), value: 'US' },
	{ label: t('locationOptions.country.canada'), value: 'CA' },
];

export const ONIX_IMAGE = {
	src: 'https://cdn.bfldr.com/LP3GHVAP/at/hzbfnfwk8xsvqfwgqhz4b5m/WB_AD_OnixFixedTechLanderDesktopCard02_830x670_20260221.png?auto=webp&format=png',
	alt: 'Image of Onix fixed implant solution',
};
